/**
 * Recipe read view (Steg 1b-ii) — read one recipe in the active cookbook.
 *
 * Composed from the prototype's editor design language (numbered section cards,
 * round step circles) but in pure READ mode. Mobile-first single column; on
 * desktop a comfortable centred reading column (the kitchen split-view is the
 * separate cook mode, Steg 4). Method step numbers are GLOBAL and continuous
 * across sections (1,2,3 …), computed by flattening the step sections in order.
 *
 * Authorization: any member may read (the API enforces `recipe:read`); a recipe
 * the caller may not see returns 404 → a generic "not found" with no existence
 * leak. The "Rediger" affordance is shown only when the caller's role on the
 * recipe's cookbook permits it — a UI mirror; the API + RLS are authoritative.
 */

import {
  formatIngredientAmount,
  roleCanEditRecipes,
  type MeResponse,
  type Recipe,
} from "@tjommi/shared";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  EntitlementRequiredError,
  ForbiddenError,
  NotFoundError,
  UnauthorizedError,
  copyRecipe,
  deleteRecipe,
  fetchRecipe,
} from "../api";
import { AddToCalendarDialog } from "../components/AddToCalendarDialog";
import { PremiumNotice } from "../components/PremiumNotice";
import { RecipeComments } from "../components/RecipeComments";
import { useCookbook } from "../cookbook/CookbookContext";
import { formatTotalTime } from "../lib/formatTime";

type DetailState =
  | { status: "loading" }
  | { status: "ok"; recipe: Recipe }
  | { status: "not-found" }
  | { status: "unauthorized" }
  | { status: "error"; message: string };

function metaParts(recipe: Recipe): string[] {
  const parts: string[] = [];
  if (recipe.totalTimeMinutes != null) parts.push(`⏱ ${formatTotalTime(recipe.totalTimeMinutes)}`);
  if (recipe.servings != null) parts.push(`🍽 ${recipe.servings} porsjoner`);
  return parts;
}

export function RecipeDetailPage({
  accessToken,
  me,
  onSessionExpired,
}: {
  accessToken: string;
  /** The viewer's own identity — the "Erfaringer" section needs it for the
   * delete-affordance gate (own comment vs. cookbook-owner moderation). */
  me: MeResponse;
  onSessionExpired: () => void;
}): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { cookbooks } = useCookbook();
  const navigate = useNavigate();
  const [state, setState] = useState<DetailState>({ status: "loading" });
  // Delete is a rare, destructive action → its own transient state (not the
  // load state machine): the button shows progress + a defensive error.
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // "Kopier til min kokebok" — a rare, non-destructive action of its own: a
  // transient pending/confirm/error state, independent of delete/load. The
  // confirmation auto-dismisses after a few seconds; it never navigates.
  const [copying, setCopying] = useState(false);
  // A string (the short inline failure) OR the parsed 402 — copying into a
  // cookbook that is already at the free recipe limit is a plan answer, not a
  // failure, and gets the calm `PremiumNotice` instead.
  const [copyError, setCopyError] = useState<string | EntitlementRequiredError | null>(null);
  const [copyDone, setCopyDone] = useState(false);
  const copyDoneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // "+ Legg til i kalenderen" (Ukeplanlegger, Slice 3) — a small dialog to
  // pick week/day/servings, then POST the planned meal. Same component backs
  // the mobile bottom sheet reached from a card's "+" quick action.
  const [addToCalendarOpen, setAddToCalendarOpen] = useState(false);
  const [addedConfirm, setAddedConfirm] = useState(false);
  const addedConfirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyDoneTimer.current) clearTimeout(copyDoneTimer.current);
      if (addedConfirmTimer.current) clearTimeout(addedConfirmTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!id) return;
    let ignore = false;
    setState({ status: "loading" });

    fetchRecipe(id, accessToken)
      .then((recipe) => {
        if (!ignore) setState({ status: "ok", recipe });
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (err instanceof UnauthorizedError) setState({ status: "unauthorized" });
        else if (err instanceof NotFoundError) setState({ status: "not-found" });
        else
          setState({
            status: "error",
            message: err instanceof Error ? err.message : "Ukjent feil",
          });
      });

    return () => {
      ignore = true;
    };
  }, [id, accessToken]);

  // ---- Non-content states ----
  if (state.status === "loading") {
    return (
      <main className="app-main">
        <div className="feed-state" data-testid="recipe-loading">
          <p>Laster oppskrift …</p>
        </div>
      </main>
    );
  }

  if (state.status === "unauthorized") {
    return (
      <main className="app-main">
        <div className="feed-state feed-error" data-testid="recipe-unauthorized">
          <h2>Økten er utløpt</h2>
          <p>Du må logge inn på nytt.</p>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: "16px" }}
            data-testid="recipe-relogin"
            onClick={onSessionExpired}
          >
            Logg inn igjen
          </button>
        </div>
      </main>
    );
  }

  if (state.status === "not-found") {
    return (
      <main className="app-main">
        <div className="feed-state" data-testid="recipe-not-found">
          <h2>Fant ikke oppskriften</h2>
          <p>Oppskriften finnes ikke, eller du har ikke tilgang til den.</p>
          <BackLink />
        </div>
      </main>
    );
  }

  if (state.status === "error") {
    return (
      <main className="app-main">
        <div className="feed-state feed-error" data-testid="recipe-error">
          <h2>Kunne ikke laste oppskriften</h2>
          <p>{state.message}</p>
          <BackLink />
        </div>
      </main>
    );
  }

  // ---- Content ----
  const { recipe } = state;
  const role = cookbooks.find((c) => c.id === recipe.cookbookId)?.role;
  const canEdit = role != null && roleCanEditRecipes(role);
  // "Kopier til min kokebok" — every member (read AND write) who does NOT own
  // the recipe's cookbook may copy it; the owner never sees it (it's already
  // theirs). Same safe-deny default as `canEdit`: an unknown role hides it.
  const canCopy = role != null && role !== "owner";
  const parts = metaParts(recipe);

  // Delete: confirm (no undo) → DELETE → back to the feed. The API + RLS are
  // authoritative; the button is only shown to an owner/write member, and a
  // 403/404/401 is still handled defensively.
  async function handleDelete(): Promise<void> {
    if (!window.confirm(`Slette «${recipe.title}»? Dette kan ikke angres.`)) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await deleteRecipe(recipe.id, accessToken);
      navigate("/");
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      if (err instanceof NotFoundError) {
        navigate("/"); // already gone (or no longer visible) → treat as done
        return;
      }
      setDeleting(false);
      setDeleteError(
        err instanceof ForbiddenError
          ? "Du har ikke rettigheter til å slette denne oppskriften."
          : "Kunne ikke slette oppskriften. Prøv igjen.",
      );
    }
  }

  // Copy: POSTs to /recipes/:id/copy (no body — the target is derived
  // server-side). LOCKED UX: on success, stay exactly on this page and show a
  // transient confirmation — no navigation. A 409 (already in your own
  // cookbook / no owned cookbook) is treated like any other failure here: an
  // inline, non-destructive error, button re-enabled.
  async function handleCopy(): Promise<void> {
    if (copyDoneTimer.current) clearTimeout(copyDoneTimer.current);
    setCopyError(null);
    setCopyDone(false);
    setCopying(true);
    try {
      await copyRecipe(recipe.id, accessToken);
      setCopying(false);
      setCopyDone(true);
      copyDoneTimer.current = setTimeout(() => setCopyDone(false), 4000);
    } catch (err) {
      setCopying(false);
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      if (err instanceof EntitlementRequiredError) {
        // 402: the copy would push YOUR OWN cookbook past the free recipe
        // limit. Nothing was copied, and nothing you already have is affected.
        setCopyError(err);
        return;
      }
      setCopyError("Kunne ikke kopiere. Prøv igjen.");
    }
  }

  // Global, continuous step numbering across all method sections.
  let stepNo = 0;

  return (
    <main className="app-main">
      <article className="recipe-read" data-testid="recipe-read">
        <div className="recipe-read-top">
          <BackLink />
          <div className="recipe-read-actions">
            <Link
              to={`/recipes/${recipe.id}/cook`}
              className="btn btn-primary"
              data-testid="cook-mode"
            >
              Kokemodus
            </Link>
            {canEdit && (
              <>
                <button
                  type="button"
                  className="btn btn-ghost"
                  data-testid="add-to-calendar-open"
                  onClick={() => setAddToCalendarOpen(true)}
                >
                  + Legg til i kalenderen
                </button>
                <Link
                  to={`/recipes/${recipe.id}/edit`}
                  className="btn btn-ghost"
                  data-testid="edit-recipe"
                >
                  Rediger
                </Link>
                <button
                  type="button"
                  className="btn btn-danger"
                  data-testid="delete-recipe"
                  disabled={deleting}
                  onClick={() => void handleDelete()}
                >
                  {deleting ? "Sletter …" : "Slett"}
                </button>
              </>
            )}
            {canCopy && (
              <button
                type="button"
                className="btn btn-ghost"
                data-testid="copy-recipe"
                disabled={copying}
                onClick={() => void handleCopy()}
              >
                {copying ? "Kopierer …" : "Kopier til min kokebok"}
              </button>
            )}
          </div>
        </div>
        {deleteError && (
          <p className="recipe-delete-error" data-testid="delete-error" role="alert">
            {deleteError}
          </p>
        )}
        {canCopy && copyDone && (
          <p className="recipe-copy-confirm" data-testid="copy-confirm" role="status">
            Kopiert til din kokebok.
          </p>
        )}
        {canCopy && copyError ? (
          copyError instanceof EntitlementRequiredError ? (
            <PremiumNotice denial={copyError} testId="copy-premium" />
          ) : (
            <p className="recipe-copy-error" data-testid="copy-error" role="alert">
              {copyError}
            </p>
          )
        ) : null}
        {addedConfirm && (
          <p
            className="recipe-copy-confirm"
            data-testid="add-to-calendar-confirm-msg"
            role="status"
          >
            Lagt til i ukeplanen.
          </p>
        )}

        {/* Hero — the real image when set (Steg 3b), else a striped placeholder. */}
        <div className="recipe-hero">
          {recipe.heroImageUrl && (
            <img
              src={recipe.heroImageUrl}
              alt=""
              className="recipe-hero-img"
              data-testid="recipe-hero-image"
            />
          )}
          {recipe.category && <span className="cat-tag">{recipe.category}</span>}
        </div>

        {/* Video link-out (video-link, Slice C) — always a link OUT to the
            source in a new tab, NEVER an embedded player/iframe (privacy: no
            third-party player is ever loaded into this page). */}
        {recipe.videoUrl && (
          <div className="recipe-video-link" data-testid="recipe-video-link">
            {recipe.videoThumbnailUrl ? (
              <a
                href={recipe.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="recipe-video-thumb-link"
                aria-label="Se video (åpnes i ny fane)"
                data-testid="recipe-video-thumb-link"
              >
                <img
                  src={recipe.videoThumbnailUrl}
                  alt=""
                  className="recipe-video-thumb-img"
                  data-testid="recipe-video-thumb-img"
                />
                <span className="recipe-video-play" aria-hidden="true">
                  ▶
                </span>
              </a>
            ) : (
              <a
                href={recipe.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="recipe-video-plain-link"
                data-testid="recipe-video-plain-link"
              >
                ▶ Se video
              </a>
            )}
          </div>
        )}

        <header className="recipe-read-head">
          <h1 data-testid="recipe-title">{recipe.title}</h1>
          {recipe.intro && <p className="recipe-read-intro">{recipe.intro}</p>}
          {parts.length > 0 && (
            <div className="recipe-read-meta" data-testid="recipe-meta">
              {parts.map((p) => (
                <span key={p}>{p}</span>
              ))}
            </div>
          )}
          {recipe.tags.length > 0 && (
            <div className="recipe-read-tags">
              {recipe.tags.map((t) => (
                <span key={t} className="chip">
                  {t}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* Ingredients */}
        {recipe.ingredientSections.length > 0 && (
          <section className="read-card" data-testid="ingredients">
            <div className="read-card-h">
              <h2>Ingredienser</h2>
            </div>
            {recipe.ingredientSections.map((sec) => (
              <div key={sec.id} className="read-sec">
                {sec.name && <div className="read-sec-name">{sec.name}</div>}
                <ul className="ing-list">
                  {sec.items.map((it) => (
                    <li key={it.id} className="ing-line">
                      <span className="ing-amount">{formatIngredientAmount(it)}</span>
                      <span className="ing-name">{it.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        )}

        {/* Method — global step numbers across sections */}
        {recipe.stepSections.length > 0 && (
          <section className="read-card" data-testid="method">
            <div className="read-card-h">
              <h2>Fremgangsmåte</h2>
            </div>
            {recipe.stepSections.map((sec) => (
              <div key={sec.id} className="read-sec">
                {sec.name && <div className="read-sec-name">{sec.name}</div>}
                {sec.items.map((step) => {
                  stepNo += 1;
                  return (
                    <div key={step.id} className="read-step">
                      <span className="step-n" data-testid="step-number">
                        {stepNo}
                      </span>
                      <div className="step-body">
                        <p className="step-text">{step.text}</p>
                        {step.imageUrl && (
                          <img
                            src={step.imageUrl}
                            alt=""
                            className="step-thumb"
                            data-testid="step-image"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </section>
        )}

        {/* Erfaringer — comments + likes (Steg F6). NOT role-gated: every
            member may comment/like; only the delete affordance is gated
            (own comment, or the cookbook owner moderating). */}
        <RecipeComments
          recipeId={recipe.id}
          accessToken={accessToken}
          mySubject={me.subject}
          canModerate={role === "owner"}
          onSessionExpired={onSessionExpired}
        />
      </article>

      {canEdit && addToCalendarOpen && (
        <AddToCalendarDialog
          cookbookId={recipe.cookbookId}
          recipeId={recipe.id}
          recipeTitle={recipe.title}
          baseServings={recipe.servings}
          accessToken={accessToken}
          onClose={() => setAddToCalendarOpen(false)}
          onSessionExpired={onSessionExpired}
          onAdded={() => {
            if (addedConfirmTimer.current) clearTimeout(addedConfirmTimer.current);
            setAddedConfirm(true);
            addedConfirmTimer.current = setTimeout(() => setAddedConfirm(false), 4000);
          }}
        />
      )}
    </main>
  );
}

function BackLink(): React.JSX.Element {
  return (
    <Link to="/" className="btn btn-ghost" data-testid="back-to-feed">
      ← Tilbake til kokeboken
    </Link>
  );
}
